import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Download, FileText, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { sandboxArtifactDownloadOptions } from "@/lib/data/react-query/sandbox-artifacts";
import type { SandboxArtifact } from "@/lib/data/schemas/sandbox-artifacts";

function ArtifactFile({ artifact }: { artifact: SandboxArtifact }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const link = useMutation(sandboxArtifactDownloadOptions(artifact.artifactId));
  const pdf = artifact.mimeType.toLowerCase() === "application/pdf";
  const size = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  }).format(artifact.sizeBytes / 1024);
  const openFile = async (disposition: "inline" | "attachment") => {
    if (disposition === "inline") {
      setPreviewUrl(undefined);
      setPreviewOpen(true);
    }
    try {
      const result = await link.mutateAsync(disposition);
      if (disposition === "inline") setPreviewUrl(result.url);
      else {
        const anchor = document.createElement("a");
        anchor.href = result.url;
        anchor.download = artifact.filename;
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch {
      // The request state renders a retryable error beside the file.
    }
  };
  return (
    <div className="bg-background rounded-lg px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="text-muted-foreground size-4 shrink-0" />
        <span className="min-w-0 flex-1 font-medium break-words">
          {artifact.filename}
        </span>
        <span className="text-muted-foreground text-xs">{size} KB</span>
        {pdf && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={link.isPending}
            onClick={() => void openFile("inline")}
          >
            Preview
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={link.isPending}
          onClick={() => void openFile("attachment")}
        >
          {link.isPending ? (
            <LoaderCircle className="size-3.5 animate-spin" />
          ) : (
            <Download className="size-3.5" />
          )}{" "}
          Download
        </Button>
      </div>
      {link.isError && !previewOpen && (
        <p role="alert" className="text-destructive mt-2 text-xs">
          This file could not be opened. Try again.
        </p>
      )}
      <Dialog
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) setPreviewUrl(undefined);
        }}
      >
        <DialogContent className="flex h-[85vh] max-w-5xl flex-col">
          <DialogHeader>
            <DialogTitle className="break-words">
              {artifact.filename}
            </DialogTitle>
            <DialogDescription>Preview the exported file.</DialogDescription>
          </DialogHeader>
          {link.isPending && <p role="status">Loading preview…</p>}
          {link.isError && (
            <div>
              <p role="alert">This file could not be opened. Try again.</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => void openFile("inline")}
              >
                Retry
              </Button>
            </div>
          )}
          {previewUrl && (
            <iframe
              title={`Preview ${artifact.filename}`}
              src={previewUrl}
              className="min-h-0 flex-1 rounded-md border"
              referrerPolicy="no-referrer"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ChatArtifacts({ artifacts }: { artifacts: SandboxArtifact[] }) {
  return (
    <div className="space-y-2" data-chat-artifacts>
      {artifacts.map((artifact) => (
        <ArtifactFile key={artifact.artifactId} artifact={artifact} />
      ))}
    </div>
  );
}
